import React, { useCallback, useEffect, useState } from 'react';
import {
  Redirect,
  Route,
  Switch, useRouteMatch,
} from 'react-router-dom';
import SearchRouter from 'app/routes/search/SearchRouter';
import VisualSearchRouter from 'app/routes/visual/search/VisualSearchRouter';
import RecsRouter from 'app/routes/recs/RecsRouter';
import VisualRecsRouter from 'app/routes/visual/recs/VisualRecsRouter';
import AccountRouter from 'app/routes/account/AccountRouter';
import Error404 from 'app/routes/404';
import { useDispatch, useSelector } from 'react-redux';
import PlaceholderSpinner from 'components/kea/placeholders/PlaceholderSpinner';
import IncorrectShop from 'components/kea/placeholders/IncorrectShop';
import SafetyRoute from 'components/kea/SafetyRoute';
import * as actions from 'actions';
import HomeScreen from 'containers/HomeScreen/HomeScreen';
import DiagnosticsScreen from 'containers/DiagnosticsScreen/DiagnosticsScreen';
import { PageAccessGate } from 'features/shell/context/PageAccessGate';
import StoreRouter from './store/StoreRouter';
import OrganizationRouter from './organization/index';
import { pluginAppInIframe } from '../../util/appUtils';
import { SEARCH_PAGE_MAP } from './searchPageMap';
import { useShellAppContext } from '../../features/shell/context/useShellAppContext';

const AppRouter = () => {
  const { auth, app } = useSelector((store) => store, undefined);
  const { authorization } = auth;
  const { currentShop } = app;

  const match = useRouteMatch();

  const spinnerIsSpinning = !currentShop || authorization.isFetching;
  if (spinnerIsSpinning) {
    return <PlaceholderSpinner />;
  }

  return (
    <Switch>
      <Route exact path={`${match.url}/`} component={(props) => <IncorrectShop {...props} authorization={authorization} />} />
      <Route path={`${match.url}/:token`} component={NestedRoutes} />
      <Route component={Error404} />
    </Switch>
  );
};

const NestedRoutes = () => {
  const dispatch = useDispatch();
  const match = useRouteMatch();
  const [wrongShop, setWrongShop] = useState(false);
  const { productProfile } = useShellAppContext();
  const isPluginProduct = productProfile?.id === 'plugin';

  const fetchShopData = useCallback((token) => {
    dispatch(actions.getSettings(token));
    dispatch(actions.loadSearchExperienceConfig(token));
    dispatch(actions.getPlatformSettings(token));
    dispatch(actions.getAlerts(token, 'search'));
    dispatch(actions.getAlerts(token, 'recs'));
    // Cloud shops are not necessarily registered in the legacy billing
    // service. Billing owns its own page-level loading; only the legacy
    // standalone plugin shell needs this global trial-status preload.
    if (isPluginProduct) dispatch(actions.getPlans(token, 'search'));
    // fields are loaded on-mount by fields/index.jsx via FieldsSlice.loadFields
  }, [dispatch, isPluginProduct]);

  const { auth, app } = useSelector((store) => store, undefined);
  const { authorization } = auth;
  const { currentShop } = app;
  const { shopMap } = authorization;

  useEffect(() => {
    // Embedded (bridge/gateway) path: shop data is loaded through the
    // bridge/gateway transport — skip legacy preload dispatch entirely.
    if (pluginAppInIframe()) {
      return;
    }
    if (shopMap[currentShop]) {
      fetchShopData(currentShop);
    } else {
      setWrongShop(true);
    }
  }, [currentShop, shopMap, fetchShopData]);

  if (wrongShop) {
    return (
      <IncorrectShop authorization={authorization} />
    );
  }

  const { url } = match;
  return (
    <>
      <Switch>
        {/* ----------------------------------------------------------------
            Canonical launch-critical routes
            ---------------------------------------------------------------- */}
        <SafetyRoute path={`${url}/home`} render={(props) => <PageAccessGate><HomeScreen {...props} /></PageAccessGate>} />
        <SafetyRoute path={`${url}/diagnostics`} render={(props) => <PageAccessGate><DiagnosticsScreen {...props} /></PageAccessGate>} />

        {/* ----------------------------------------------------------------
            Legacy compatibility redirects — derived from SEARCH_PAGE_MAP
            ---------------------------------------------------------------- */}
        {SEARCH_PAGE_MAP.flatMap((page) => page.legacyAliases.map((alias) => (
          <Route key={alias} exact path={`${url}${alias}`} render={() => <Redirect to={`${url}${page.path}`} />} />
        )))}
        <Route exact path={`${url}/search/integration-wizard`} render={() => <Redirect to={`${url}/search/setup`} />} />
        {/* ----------------------------------------------------------------
            Legacy search sub-routes — redirect to canonical home.
            These intercept before SearchRouter to guarantee zero legacy
            component render on the embedded production path.
            Note: /search/analytic/* and /search/customization/* are NOT
            redirected here because Task 10J adds bridge-backed routes at
            those paths via SearchRouter.
            ---------------------------------------------------------------- */}
        <Route path={`${url}/search/dashboard`} render={() => <Redirect to={`${url}/search/analytic/health`} />} />
        <Route path={`${url}/search/welcome`} render={() => <Redirect to={`${url}/home`} />} />
        <Route path={`${url}/search`} component={SearchRouter} />
        {isPluginProduct && (
          <Route
            path={[
              `${url}/store`,
              `${url}/visual_search`,
              `${url}/recs`,
              `${url}/visual_recs`,
              `${url}/account`,
              `${url}/organization`,
            ]}
            render={() => <Redirect to={`${url}/home`} />}
          />
        )}
        {!isPluginProduct && <Route path={`${url}/store`} component={StoreRouter} />}
        {!isPluginProduct && <Route path={`${url}/visual_search`} component={VisualSearchRouter} />}
        {!isPluginProduct && <Route path={`${url}/recs`} component={RecsRouter} />}
        {!isPluginProduct && <Route path={`${url}/visual_recs`} component={VisualRecsRouter} />}
        {!isPluginProduct && <Route path={`${url}/account`} component={AccountRouter} />}
        {!isPluginProduct && <Route path={`${url}/organization`} component={OrganizationRouter} />}
      </Switch>
    </>
  );
};

export default AppRouter;
